import { useMemo } from "react";
import { ClipboardCheck } from "lucide-react";

import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { hasPermission, isIntakeManager } from "@/lib/permissions";
import { ROUTES } from "@/lib/routes";
import { useMyIntakeMissing, useMyRadiologists } from "@/features/intake/hooks/useIntake";

// Temporary kill-switch for the red "missing documents" badge. Typed as
// `boolean` (not the literal `false`) so the disabled branch still type-checks.
const SHOW_INTAKE_MISSING_BADGE: boolean = false;

type IntakeNavItemProps = {
  roles: string[];
  isActive: boolean;
  onNavigate: (to: string) => void;
};

/**
 * Intake entry, shown to intake managers (god/admin, who pick any radiologist)
 * and to any radiologist-role account (intake is self-service — a rad with no
 * linked record onboards via the wizard). For self-onboarding radiologists it
 * can carry a red badge with the total missing documents across their linked
 * radiologists, plus an onboarding nudge dot.
 */
export function IntakeNavItem({ roles, isActive, onNavigate }: IntakeNavItemProps) {
  // Managers (god/admin) pick any radiologist — they never self-onboard, so the
  // "my radiologists" queries, missing-doc badge, and nudge dot don't apply.
  const manager = isIntakeManager(roles);
  const canAccessIntake = hasPermission(roles, "access_intake");
  const { rads, isSuccess } = useMyRadiologists(canAccessIntake && !manager);
  const radIds = useMemo(() => rads.map((r) => r.id), [rads]);
  // Gated on the kill-switch too: with the badge off the result is never
  // rendered, so the request is pure waste.
  const { missing, isError: missingError } = useMyIntakeMissing(
    radIds,
    SHOW_INTAKE_MISSING_BADGE && canAccessIntake && !manager && radIds.length > 0,
  );

  // Nudge: a red dot when the rad still needs the intake walkthrough — no linked
  // rad yet, or a linked rad that hasn't finished onboarding. Matches when the
  // wizard shows on /intake. Only once the roster is confirmed (avoid a flash).
  const needsOnboarding =
    !manager && isSuccess && (rads.length === 0 || rads.some((r) => !r.onboarding_completed_at));

  // Role gate only (matches the /intake PermissionRoute). Intake is now
  // self-service: a radiologist with no linked rad still sees the entry so they
  // can create + onboard their own record via the wizard.
  if (!canAccessIntake) return null;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton tooltip="Intake" isActive={isActive} onClick={() => onNavigate(ROUTES.INTAKE)} className="relative">
        <ClipboardCheck />
        <span>Intake</span>
        {SHOW_INTAKE_MISSING_BADGE && !manager && !missingError && missing != null && missing > 0 ? (
          // Temporarily hidden: flip SHOW_INTAKE_MISSING_BADGE to re-enable.
          <span className="pointer-events-none absolute right-1 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white">
            {missing}
          </span>
        ) : needsOnboarding ? (
          // Attention dot nudging the rad to start/finish intake (no count).
          <span className="pointer-events-none absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-destructive" aria-hidden />
        ) : null}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
